import Fuse, { IFuseOptions } from 'fuse.js';
import { doubleMetaphone } from 'double-metaphone';

/**
 * Generic node interface for search
 */
export interface SearchableNode {
  nodeId: number;
  nodeTypeId?: number;
  data?: {
    name?: string;
    normalizedName?: string;
    [key: string]: unknown;
  };
}

/**
 * Preprocessed node with phonetic data for search
 */
interface PreprocessedNode<T extends SearchableNode> {
  node: T;
  normalizedName: string | null;
  phoneticName: string | null;
  name: string | null;
}

/**
 * Search context that holds precomputed and cached data.
 */
export class NodesSearchContext<T extends SearchableNode = SearchableNode> {
  phoneticCache: Map<string, string>;
  nodeIdMap: Map<number, T>;
  preprocessedNodes: PreprocessedNode<T>[];
  fuse: Fuse<PreprocessedNode<T>>;

  constructor(
    phoneticCache: Map<string, string>,
    nodeIdMap: Map<number, T>,
    preprocessedNodes: PreprocessedNode<T>[],
    fuse: Fuse<PreprocessedNode<T>>
  ) {
    this.phoneticCache = phoneticCache;
    this.nodeIdMap = nodeIdMap;
    this.preprocessedNodes = preprocessedNodes;
    this.fuse = fuse;
  }
}

/**
 * Remove diacritics from a string (e.g., 'sri' -> 'sri')
 * This allows 'sri' to match 'sri' in search
 *
 * Unicode NFD normalization separates combining marks from base characters.
 * Then we remove all combining diacritical marks (U+0300 to U+036F).
 */
export function removeDiacritics(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u0100-\u017F]/g, (char) => {
      const latinExtendedA: Record<string, string> = {
        '\u0100': 'A', '\u0101': 'a', '\u0102': 'A', '\u0103': 'a', '\u0104': 'A', '\u0105': 'a',
        '\u0106': 'C', '\u0107': 'c', '\u0108': 'C', '\u0109': 'c', '\u010A': 'C', '\u010B': 'c', '\u010C': 'C', '\u010D': 'c',
        '\u010E': 'D', '\u010F': 'd', '\u0110': 'D', '\u0111': 'd',
        '\u0112': 'E', '\u0113': 'e', '\u0114': 'E', '\u0115': 'e', '\u0116': 'E', '\u0117': 'e', '\u0118': 'E', '\u0119': 'e', '\u011A': 'E', '\u011B': 'e',
        '\u011C': 'G', '\u011D': 'g', '\u011E': 'G', '\u011F': 'g', '\u0120': 'G', '\u0121': 'g', '\u0122': 'G', '\u0123': 'g',
        '\u0124': 'H', '\u0125': 'h', '\u0126': 'H', '\u0127': 'h',
        '\u0128': 'I', '\u0129': 'i', '\u012A': 'I', '\u012B': 'i', '\u012C': 'I', '\u012D': 'i', '\u012E': 'I', '\u012F': 'i', '\u0130': 'I', '\u0131': 'i',
        '\u0132': 'IJ', '\u0133': 'ij',
        '\u0134': 'J', '\u0135': 'j',
        '\u0136': 'K', '\u0137': 'k', '\u0138': 'k',
        '\u0139': 'L', '\u013A': 'l', '\u013B': 'L', '\u013C': 'l', '\u013D': 'L', '\u013E': 'l', '\u013F': 'L', '\u0140': 'l', '\u0141': 'L', '\u0142': 'l',
        '\u0143': 'N', '\u0144': 'n', '\u0145': 'N', '\u0146': 'n', '\u0147': 'N', '\u0148': 'n', '\u0149': 'n', '\u014A': 'N', '\u014B': 'n',
        '\u014C': 'O', '\u014D': 'o', '\u014E': 'O', '\u014F': 'o', '\u0150': 'O', '\u0151': 'o', '\u0152': 'OE', '\u0153': 'oe',
        '\u0154': 'R', '\u0155': 'r', '\u0156': 'R', '\u0157': 'r', '\u0158': 'R', '\u0159': 'r',
        '\u015A': 'S', '\u015B': 's', '\u015C': 'S', '\u015D': 's', '\u015E': 'S', '\u015F': 's', '\u0160': 'S', '\u0161': 's',
        '\u0162': 'T', '\u0163': 't', '\u0164': 'T', '\u0165': 't', '\u0166': 'T', '\u0167': 't',
        '\u0168': 'U', '\u0169': 'u', '\u016A': 'U', '\u016B': 'u', '\u016C': 'U', '\u016D': 'u', '\u016E': 'U', '\u016F': 'u', '\u0170': 'U', '\u0171': 'u', '\u0172': 'U', '\u0173': 'u',
        '\u0174': 'W', '\u0175': 'w',
        '\u0176': 'Y', '\u0177': 'y', '\u0178': 'Y',
        '\u0179': 'Z', '\u017A': 'z', '\u017B': 'Z', '\u017C': 'z', '\u017D': 'Z', '\u017E': 'z',
      };
      return latinExtendedA[char] || char;
    });
}

/**
 * Normalize a string (lowercase, remove extra spaces, remove diacritics)
 */
export function normalizeText(text: string): string {
  return removeDiacritics(text.toLowerCase().replace(/\s+/g, ' ').trim());
}

/**
 * Generate phonetic representation of text (with caching)
 */
function getCachedPhoneticText(text: string | null | undefined, cache: Map<string, string>): string | null {
  if (text == null) {
    return null;
  }

  const normalized = normalizeText(text);
  if (!cache.has(normalized)) {
    cache.set(normalized, doubleMetaphone(normalized).join(' '));
  }
  return cache.get(normalized)!;
}

/**
 * Create a new `NodesSearchContext` by precomputing necessary data.
 */
export function createNodesSearchContext<T extends SearchableNode>(nodes: T[]): NodesSearchContext<T> | null {
  if (!nodes.length) {
    return null;
  }

  const phoneticCache = new Map<string, string>();
  const nodeIdMap = new Map<number, T>();

  const preprocessedNodes = nodes.map(node => {
    const rawName = node.data?.normalizedName || node.data?.name || null;
    const normalizedName = rawName ? removeDiacritics(rawName) : null;
    const phoneticName = getCachedPhoneticText(normalizedName, phoneticCache);
    nodeIdMap.set(node.nodeId, node);
    return {
      node,
      normalizedName,
      phoneticName,
      name: node.data?.name || null,
    };
  });

  const fuseKeys = [
    { name: 'normalizedName' as const, weight: 0.7 },
    { name: 'phoneticName' as const, weight: 0.7 },
    { name: 'name' as const, weight: 0.5 },
  ];
  const fuseOptions: IFuseOptions<PreprocessedNode<T>> = {
    includeScore: true,
    threshold: 0.3,
    ignoreLocation: true,
    keys: fuseKeys,
  };
  const fuse = new Fuse(
    preprocessedNodes,
    fuseOptions,
    Fuse.createIndex(fuseKeys, preprocessedNodes)
  );
  return new NodesSearchContext(phoneticCache, nodeIdMap, preprocessedNodes, fuse);
}

/**
 * Find an exact node ID match (O(1) lookup).
 */
function matchExactNodeId<T extends SearchableNode>(
  query: string,
  context: NodesSearchContext<T>,
  restrictToNodeTypeId?: number
): T[] {
  const nodeId = parseInt(query, 10);
  if (isNaN(nodeId)) {
    return [];
  }

  const node = context.nodeIdMap.get(nodeId);
  if (!node) {
    return [];
  }

  if (restrictToNodeTypeId !== undefined && node.nodeTypeId !== restrictToNodeTypeId) {
    return [];
  }

  return [node];
}

/**
 * Perform a ranked search using exact match (if applicable) and Fuse.js fuzzy search.
 */
export function searchNodes<T extends SearchableNode>(
  queryStr: string | null | undefined,
  context: NodesSearchContext<T> | null,
  restrictToNodeTypeId?: number
): { nodes: T[]; nodesAreSorted: boolean } {
  if (context?.fuse == null) {
    return { nodes: [], nodesAreSorted: true };
  }

  if (!queryStr) {
    return {
      nodes: context.preprocessedNodes.map(p => p.node),
      nodesAreSorted: true,
    };
  }

  const normalizedText = normalizeText(queryStr);

  const exactMatch = matchExactNodeId(queryStr, context, restrictToNodeTypeId);
  if (exactMatch.length) {
    return {
      nodes: exactMatch,
      nodesAreSorted: true,
    };
  }

  const phoneticQuery = getCachedPhoneticText(normalizedText, context.phoneticCache);

  const seenIds = new Set<number>();
  const results: T[] = [];

  const textResults = context.fuse.search(normalizedText);
  for (const res of textResults) {
    if (!seenIds.has(res.item.node.nodeId)) {
      seenIds.add(res.item.node.nodeId);
      results.push(res.item.node);
    }
  }

  if (phoneticQuery) {
    const phoneticResults = context.fuse.search(phoneticQuery);
    for (const res of phoneticResults) {
      if (!seenIds.has(res.item.node.nodeId)) {
        seenIds.add(res.item.node.nodeId);
        results.push(res.item.node);
      }
    }
  }

  return {
    nodes: results,
    nodesAreSorted: true,
  };
}

/**
 * Simple search function for when you don't need the full context machinery.
 * Creates a temporary context and searches immediately.
 */
export function quickSearch<T extends SearchableNode>(
  nodes: T[],
  query: string | null | undefined,
  restrictToNodeTypeId?: number
): T[] {
  const context = createNodesSearchContext(nodes);
  if (!context) {
    return [];
  }
  return searchNodes(query, context, restrictToNodeTypeId).nodes;
}

/**
 * Reorder an already-filtered node list by search relevance to `query`, using
 * the same Fuse text-then-phonetic ranking as searchNodes (closest to
 * jnana-native's ordering). Nodes the search doesn't surface keep their original
 * order at the end. Returns the list unchanged when there's no query.
 */
export function sortByRelevance<T extends SearchableNode>(
  nodes: T[],
  query: string | null | undefined
): T[] {
  if (!query || !query.trim() || nodes.length < 2) {
    return nodes;
  }
  const context = createNodesSearchContext(nodes);
  if (!context) {
    return nodes;
  }
  const ranked = searchNodes(query, context).nodes;
  // Dedup assumes unique nodeIds in `nodes` (guaranteed by the filter framework)
  // and no duplicates from searchNodes (Fuse doesn't repeat items).
  const rankedIds = new Set(ranked.map((n) => n.nodeId));
  const rest = nodes.filter((n) => !rankedIds.has(n.nodeId));
  return [...ranked, ...rest];
}
